import {
  RepaymentCalculatorFormValue,
  RepaymentRateChange,
  SpecialRepayment,
} from "./types";

export interface Data {
  x: number;
  y: number;
  interest: number;
  date: Date;
}

export interface RepaymentPlan {
  data: Data[];
  startDate: Date;
  endDate: Date;
  monthlyRepayment: number;
  interestAmount: number;
  durationYears: number;
  durationMonths: number;
  residualDept: number;
  totalSpecialRepayment: number;
}

export interface CompleteRepaymentPlan {
  repayment: RepaymentPlan;
  secondaryRepayment?: RepaymentPlan;
  totalDurationYears: number;
  values: RepaymentCalculatorFormValue;
}

const getRepaymentRateForYear = (
  initialRepaymentRate: number,
  repaymentRateChange: RepaymentRateChange[],
  year: number,
): number => {
  return (
    repaymentRateChange.reverse().find((r) => r.year <= year)?.repaymentRate ||
    initialRepaymentRate
  );
};

export const getRepaymentRate = (
  loanAmount: number,
  interestRate: number,
  monthlyRepayment: number,
) => {
  return Math.min(
    100,
    Math.max(0, 100 * ((12 * monthlyRepayment) / loanAmount) - interestRate),
  );
};

export const getMonthlyRepayment = (
  loanAmount: number,
  repaymentRate: number,
  annualInterestRate: number,
): number => {
  return (loanAmount * ((repaymentRate + annualInterestRate) / 100)) / 12;
};

export const calculateRepayment = (
  values: RepaymentCalculatorFormValue,
  startDate: Date,
): CompleteRepaymentPlan => {
  const sortedRepaymentRateChange = values.repaymentRateChange.sort(
    (a, b) => a.year - b.year,
  );
  const repayment = calculateRepaymentPlan(
    values.loanAmount,
    values.interestRate,
    values.repaymentRate,
    values.yearlySpecialRepayment,
    values.specialRepayments,
    sortedRepaymentRateChange,
    startDate,
    values.fixedInterestPeriodYears * 12,
  );
  let secondaryRepayment: ReturnType<typeof calculateRepaymentPlan> | undefined;
  let totalDurationYears = repayment.durationYears;

  if (repayment.residualDept) {
    const secondaryStartDate = new Date(startDate);
    secondaryStartDate.setFullYear(
      secondaryStartDate.getFullYear() + values.fixedInterestPeriodYears,
    );

    secondaryRepayment = calculateRepaymentPlan(
      repayment.residualDept,
      values.secondaryInterestRate,
      values.secondaryRepaymentRate,
      values.yearlySpecialRepayment,
      values.specialRepayments,
      sortedRepaymentRateChange,
      secondaryStartDate,
    );

    secondaryRepayment.data = secondaryRepayment.data.map((d) => ({
      x: d.x + repayment.durationMonths / 12,
      y: d.y,
      interest: d.interest + repayment.interestAmount,
      date: d.date,
    }));

    const startX = secondaryRepayment.data[0]?.x;

    if (startX) {
      for (let i = 0; i < startX; i++) {
        secondaryRepayment.data.unshift({ x: undefined, y: undefined } as any);
      }
    }

    totalDurationYears += secondaryRepayment.durationYears;
  }

  return {
    repayment,
    secondaryRepayment,
    totalDurationYears,
    values,
  };
};

export function calculateRepaymentPlan(
  loanAmount: number,
  annualInterestRate: number,
  initialRepaymentRate: number,
  yearlySpecialRepayment: number = 0,
  specialRepayments: SpecialRepayment[],
  repaymentRateChange: RepaymentRateChange[],
  startDate: Date,
  maxDurationMonths?: number,
): RepaymentPlan {
  const monthlyInterestRate = annualInterestRate / 12 / 100;

  let date = new Date(startDate);
  date.setMilliseconds(0);

  const data: Data[] = [
    {
      x: 0,
      y: loanAmount,
      date,
      interest: 0,
    },
  ];

  const specialRepaymentsByYear = specialRepayments.reduce((all, current) => {
    all.set(current.year, current.amount);
    return all;
  }, new Map());

  let i = 0;
  let remainingAmount = loanAmount;
  let interestAmount = 0;
  let totalSpecialRepayment = 0;

  const maxMonths = maxDurationMonths || 1200;
  let addData = false;

  const initialMonthlyRepayment = getMonthlyRepayment(
    loanAmount,
    initialRepaymentRate,
    annualInterestRate,
  );

  let monthlyRepayment = initialMonthlyRepayment;

  while (remainingAmount > 0 && i < maxMonths) {
    i++;
    date = new Date(startDate);
    date.setMilliseconds(0);
    date.setMonth(date.getMonth() + i);
    const repaymentRate = getRepaymentRateForYear(
      initialRepaymentRate,
      repaymentRateChange,
      date.getFullYear(),
    );
    monthlyRepayment = getMonthlyRepayment(
      loanAmount,
      repaymentRate,
      annualInterestRate,
    );
    const interest = remainingAmount * monthlyInterestRate;

    interestAmount += interest;
    remainingAmount -= monthlyRepayment - interest;

    // substract special payments the last month of the year
    if (i % 12 === 11) {
      totalSpecialRepayment += Math.min(
        Math.max(remainingAmount, 0),
        yearlySpecialRepayment +
          (specialRepaymentsByYear.get(date.getFullYear()) ?? 0),
      );
      remainingAmount -= yearlySpecialRepayment;

      if (specialRepaymentsByYear.has(date.getFullYear())) {
        remainingAmount -= specialRepaymentsByYear.get(date.getFullYear());
      }
    }

    remainingAmount = Math.max(remainingAmount, 0);

    // for performance we only add the first data points of every year to the chart
    addData = i % 12 === 0;

    if (addData) {
      data.push({
        date,
        x: i / 12,
        y: Math.floor(remainingAmount),
        interest: Math.floor(interestAmount),
      });
    }
  }

  if (!addData) {
    data.push({
      date,
      x: i / 12,
      y: Math.floor(remainingAmount),
      interest: Math.floor(interestAmount),
    });
  }

  return {
    data,
    endDate: date,
    startDate,
    monthlyRepayment: initialMonthlyRepayment,
    interestAmount,
    durationYears: i / 12,
    durationMonths: i,
    residualDept: remainingAmount,
    totalSpecialRepayment,
  };
}
